/**
 * affiliate-partners-page.tsx — Platform → Tiếp thị liên kết → Đối tác. Route `/platform/affiliate/partners`.
 * Danh sách + chi tiết (hồ sơ che, xem CCCD/STK đầy đủ có lý do + nhật ký, tạm khoá / mở, điều chỉnh ±).
 */
import { TaxCertificateCard } from '@/pages/affiliate/tax-certificate'
import { useState } from 'react'
import { toast } from 'sonner'
import { Eye, Loader2, Search } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { DataTable, type Column } from '@/components/shared/data-table'
import { Pagination } from '@/components/shared/pagination'
import { Loading } from '@/components/shared/feedback'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { formatVnd } from '@/lib/format-money'
import { platformApiError } from '@/lib/platform-client'
import {
  commissionStatusLabel, formatSignedVnd, referralSourceLabel, withdrawalStatusLabel, WITHDRAWAL_STATUS_TONE,
} from '@/lib/affiliate'
import {
  loadPartnerTaxCertificate, revealPartner, useAdminPartners, useCreateAdjustment, usePartnerDetail, useSetPartnerStatus, type AdminPartner,
} from '@/hooks/use-platform-affiliate'
import { FilterChips, fmtDate, ReasonDialog } from './affiliate-shared'

export function AffiliatePartnersPage() {
  const [q, setQ] = useState('')
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)
  const [openId, setOpenId] = useState<string | null>(null)
  const { data, isLoading } = useAdminPartners({ q, status, page })

  const columns: Column<AdminPartner>[] = [
    {
      key: 'name', header: 'Đối tác',
      cell: (p) => (
        <div>
          <p className="font-medium">{p.partnerType === 'company' && p.companyName ? p.companyName : p.fullName}</p>
          <p className="text-xs text-muted-foreground">{p.email} · {p.phone}</p>
        </div>
      ),
    },
    { key: 'code', header: 'Mã', cell: (p) => <span className="font-mono text-xs">{p.code}</span> },
    { key: 'type', header: 'Loại', cell: (p) => (p.partnerType === 'company' ? 'Doanh nghiệp' : 'Cá nhân') },
    { key: 'org', header: 'Tổ chức của đối tác', cell: (p) => <span className="text-xs">{p.orgName ?? p.orgId}</span> },
    { key: 'refs', header: 'Khách', align: 'right', cell: (p) => p.referrals },
    { key: 'earned', header: 'Hoa hồng', align: 'right', cell: (p) => formatSignedVnd(p.earned) },
    {
      key: 'status', header: 'Trạng thái',
      cell: (p) => <Badge variant={p.status === 'active' ? 'success' : 'destructive'}>{p.status === 'active' ? 'Hoạt động' : 'Tạm khoá'}</Badge>,
    },
    { key: 'at', header: 'Đăng ký', cell: (p) => fmtDate(p.createdAt) },
  ]

  return (
    <div className="space-y-5">
      <PageHeader title="Đối tác tiếp thị liên kết" description="Người dùng eCDP đã đăng ký làm đối tác. Thông tin nhạy cảm luôn được che." />
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-xs">
          <Search className="absolute left-2.5 top-2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-8" placeholder="Tìm tên, email, SĐT, mã…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1) }} />
        </div>
        <FilterChips value={status} onChange={(v) => { setStatus(v); setPage(1) }} options={[['', 'Tất cả'], ['active', 'Hoạt động'], ['suspended', 'Tạm khoá']]} />
      </div>
      <DataTable columns={columns} rows={data?.items ?? []} loading={isLoading} rowKey={(p) => p.id} onRowClick={(p) => setOpenId(p.id)} emptyTitle="Chưa có đối tác" />
      {data && data.total > data.limit && <Pagination page={page} limit={data.limit} total={data.total} onPageChange={setPage} />}
      <PartnerDetailDialog id={openId} onClose={() => setOpenId(null)} />
    </div>
  )
}

function PartnerDetailDialog({ id, onClose }: { id: string | null; onClose: () => void }) {
  const { data, isLoading } = usePartnerDetail(id)
  const setStatus = useSetPartnerStatus()
  const adjust = useCreateAdjustment()
  const [action, setAction] = useState<'reveal' | 'suspend' | 'activate' | 'adjust' | null>(null)
  const [revealed, setRevealed] = useState<{ idNumber: string | null; bankAccount: string | null } | null>(null)
  const [adjAmount, setAdjAmount] = useState('')
  const p = data?.partner

  const close = () => { setRevealed(null); setAction(null); onClose() }

  return (
    <Dialog open={!!id} onOpenChange={(v) => { if (!v) close() }}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader><DialogTitle>Chi tiết đối tác</DialogTitle></DialogHeader>
        {isLoading || !data || !p ? <Loading /> : (
          <div className="space-y-4 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-base font-medium">{p.partnerType === 'company' && p.companyName ? p.companyName : p.fullName}</span>
              <Badge variant={p.status === 'active' ? 'success' : 'destructive'}>{p.status === 'active' ? 'Hoạt động' : 'Tạm khoá'}</Badge>
              <span className="font-mono text-xs">Mã {p.code}</span>
              <div className="ml-auto flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setAction('adjust')}>Điều chỉnh ±</Button>
                {p.status === 'active'
                  ? <Button size="sm" variant="destructive" onClick={() => setAction('suspend')}>Tạm khoá</Button>
                  : <Button size="sm" onClick={() => setAction('activate')}>Mở khoá</Button>}
              </div>
            </div>
            {p.statusReason && <p className="text-xs text-muted-foreground">Lý do trạng thái: {p.statusReason}</p>}

            <div className="grid gap-4 md:grid-cols-2">
              <Card className="space-y-1 p-4">
                <p className="font-medium">Hồ sơ</p>
                <p>{p.fullName} · {p.phone} · {p.email}</p>
                <p className="text-muted-foreground">{p.address}</p>
                {p.partnerType === 'company' && <p>MST: {p.taxCode}</p>}
                <p className="text-xs text-muted-foreground">Tổ chức: {p.orgName ?? p.orgId} · đăng ký {fmtDate(p.createdAt)}</p>
              </Card>
              <Card className="space-y-1 p-4">
                <div className="flex items-center justify-between">
                  <p className="font-medium">Nhận tiền & thuế</p>
                  <Button size="sm" variant="ghost" onClick={() => setAction('reveal')}><Eye className="h-3.5 w-3.5" /> Xem đầy đủ</Button>
                </div>
                {p.partnerType === 'individual' && <p>CCCD: {revealed?.idNumber ?? p.idNumberMasked}</p>}
                <p>{p.bankName}{p.bankBranch ? ` — ${p.bankBranch}` : ''}: {revealed?.bankAccount ?? p.bankAccountMasked}</p>
                <p>Chủ TK: {p.bankAccountHolder}</p>
                {p.partnerType === 'individual' && <p>Cam kết mẫu 08: {p.pitCommitment ? 'Đã nộp (kế toán đối chiếu)' : 'Chưa'}</p>}
                {p.payoutInfoChangedAt && <p className="text-xs text-warning">Đổi thông tin nhận tiền lúc {fmtDate(p.payoutInfoChangedAt)} — kiểm tra lại trước khi chi.</p>}
              </Card>
            </div>

            <TaxCertificateCard load={(params) => loadPartnerTaxCertificate(p.id, params)} errorText={platformApiError} />

            <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
              {[
                ['Lượt nhấp', data.summary.clicks.toLocaleString('vi-VN')],
                ['Lead', data.summary.leads.toLocaleString('vi-VN')],
                ['Khách / trả tiền', `${data.summary.referrals} / ${data.summary.payingCustomers}`],
                ['Đang giữ', formatVnd(data.summary.commission.holding)],
                ['Có thể rút', formatSignedVnd(data.summary.commission.available)],
              ].map(([l, v]) => (
                <Card key={l} className="p-3"><p className="text-xs text-muted-foreground">{l}</p><p className="font-medium">{v}</p></Card>
              ))}
            </div>

            <section className="space-y-1">
              <p className="font-medium">Khách đã giới thiệu ({data.referrals.length})</p>
              {data.referrals.length === 0 ? <p className="text-muted-foreground">Chưa có</p> : (
                <div className="max-h-48 overflow-y-auto rounded border">
                  {data.referrals.map((r) => (
                    <div key={r.id} className="flex justify-between gap-2 border-b px-3 py-1.5 last:border-0">
                      <span>{r.orgName ?? r.orgId}</span>
                      <span className="text-xs text-muted-foreground">
                        {referralSourceLabel(r.source)} · {fmtDate(r.attributedAt)} {r.lockedAt ? '· đã trả tiền' : ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </section>
            <section className="space-y-1">
              <p className="font-medium">Hoa hồng gần đây</p>
              <div className="max-h-48 overflow-y-auto rounded border">
                {data.commissions.length === 0 ? <p className="p-3 text-muted-foreground">Chưa có</p> : data.commissions.map((c) => (
                  <div key={c.id} className="flex justify-between gap-2 border-b px-3 py-1.5 last:border-0">
                    <span>{c.kind === 'adjustment' ? `Điều chỉnh: ${c.reason}` : c.customer}</span>
                    <span className="text-xs">{formatSignedVnd(c.amount)} · {commissionStatusLabel(c.status)}</span>
                  </div>
                ))}
              </div>
            </section>
            <section className="space-y-1">
              <p className="font-medium">Lệnh rút</p>
              <div className="max-h-40 overflow-y-auto rounded border">
                {data.withdrawals.length === 0 ? <p className="p-3 text-muted-foreground">Chưa có</p> : data.withdrawals.map((w) => (
                  <div key={w.id} className="flex justify-between gap-2 border-b px-3 py-1.5 last:border-0">
                    <span className="font-mono text-xs">{w.code}</span>
                    <span className="text-xs">{formatVnd(w.amount)} · <Badge variant={WITHDRAWAL_STATUS_TONE[w.status]}>{withdrawalStatusLabel(w.status)}</Badge></span>
                  </div>
                ))}
              </div>
            </section>

            <ReasonDialog
              open={action === 'reveal'}
              title="Xem CCCD / số tài khoản đầy đủ"
              description="Thao tác được ghi vào nhật ký Platform."
              confirmLabel="Xem"
              onClose={() => setAction(null)}
              onConfirm={async (reason) => {
                try {
                  setRevealed(await revealPartner(p.id, reason))
                  setAction(null)
                } catch (e) {
                  toast.error(platformApiError(e))
                }
              }}
            />
            <ReasonDialog
              open={action === 'suspend' || action === 'activate'}
              title={action === 'suspend' ? 'Tạm khoá đối tác' : 'Mở khoá đối tác'}
              description={action === 'suspend' ? 'Link giới thiệu, mã ưu đãi đi kèm và rút tiền của đối tác ngừng hoạt động.' : undefined}
              destructive={action === 'suspend'}
              pending={setStatus.isPending}
              onClose={() => setAction(null)}
              onConfirm={(reason) => setStatus.mutate({ id: p.id, status: action === 'suspend' ? 'suspended' : 'active', reason }, {
                onSuccess: () => { toast.success('Đã cập nhật trạng thái'); setAction(null) },
                onError: (e) => toast.error(platformApiError(e)),
              })}
            />
            <ReasonDialog
              open={action === 'adjust'}
              title="Điều chỉnh hoa hồng"
              description="Số dương = cộng, số âm = trừ (vd thu hồi do gian lận). Có hiệu lực ngay vào số dư có thể rút."
              pending={adjust.isPending}
              onClose={() => setAction(null)}
              onConfirm={(reason) => {
                const amount = Number(adjAmount.replace(/[^\d-]/g, ''))
                if (!Number.isInteger(amount) || amount === 0) { toast.error('Nhập số tiền khác 0'); return }
                adjust.mutate({ partnerId: p.id, amount, reason }, {
                  onSuccess: () => { toast.success('Đã tạo điều chỉnh'); setAction(null); setAdjAmount('') },
                  onError: (e) => toast.error(platformApiError(e)),
                })
              }}
            >
              <div className="space-y-1.5">
                <Label htmlFor="adj-amount">Số tiền (VND)</Label>
                <Input id="adj-amount" value={adjAmount} onChange={(e) => setAdjAmount(e.target.value)} placeholder="-150000 hoặc 200000" />
              </div>
            </ReasonDialog>
            {(setStatus.isPending || adjust.isPending) && <Loader2 className="h-4 w-4 animate-spin" />}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
